import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useUpdateUserItem } from '@/hooks/useUserItems';
import { UserItem } from '@/types/userItem';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

interface InlinePriceEditorProps {
  item: UserItem;
  children: React.ReactNode;
  /** Extra class on the wrapper span. */
  className?: string;
}

/**
 * Wraps the price display so a click swaps into an inline text input.
 * Enter / blur → save. Escape → cancel. No modal.
 */
export function InlinePriceEditor({ item, children, className }: InlinePriceEditorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateItem = useUpdateUserItem();

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setValue(item.asking_price_cents ? (item.asking_price_cents / 100).toFixed(2) : '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setValue('');
  };

  const commit = async () => {
    if (saving) return;
    const trimmed = value.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      toast.error('Enter a valid price');
      inputRef.current?.focus();
      return;
    }
    const nextCents = parsed === null ? null : Math.round(parsed * 100);
    if (nextCents === (item.asking_price_cents ?? null)) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      const patch: Record<string, unknown> = { asking_price_cents: nextCents };
      // Auto-list when giving a price to an unlisted item
      if (nextCents != null && item.visibility !== 'listed') {
        patch.visibility = 'listed';
        patch.is_listed = true;
      }
      await updateItem.mutateAsync({ id: item.id, ...(patch as any) });
      setEditing(false);
    } catch (err) {
      console.error('inline price save failed', err);
      toast.error('Could not update price');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        onPointerDownCapture={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        onClickCapture={startEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startEdit(e as unknown as React.MouseEvent);
          }
        }}
        title="Click to edit price"
        className={cn(
          'inline-flex items-center cursor-text rounded hover:bg-white/[0.06] -mx-1 px-1 py-0.5 min-h-[24px]',
          className
        )}
      >
        {children}
      </span>
    );
  }


  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      onPointerDownCapture={(e) => e.stopPropagation()}
      onMouseDownCapture={(e) => e.stopPropagation()}
      onClickCapture={(e) => e.stopPropagation()}
    >

      <span className="text-active-gold text-[16px] font-bold">$</span>
      <input
        ref={inputRef}
        {...PRICE_INPUT_PROPS}
        type="text"
        inputMode="decimal"
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        placeholder="0.00"
        className="w-20 bg-white/[0.06] border border-active-gold/40 rounded px-1.5 py-0.5 text-[15px] font-bold text-active-gold tabular-nums outline-none focus:border-active-gold"
      />
      {saving && <Loader2 className="w-3 h-3 animate-spin text-active-gold" />}
    </span>
  );
}
